import { useRouter } from 'expo-router'
import { useTranslation } from 'react-i18next'

import { BuyOrderStatus } from '@/constants/common'
import { formatLocalFiatAmount, formatMarketPrice, formatTokenAmount } from '@/config/marketPresentation'
import { BuyQueryResult, SellQueryResult } from '@/types/common'
import { formatDate } from '@/utils/formatter'
import { getPaymentMethod } from '@/utils/payment'
import DescriptionListItem from '@/components/DescriptionListItem'
import { usePaymentDetailScreen } from '@/components/orders/PaymentDetailScreen'
import PaymentSectionDetail from '@/components/orders/PaymentSectionDetail'
import SectionHeader from '@/components/SectionHeader'

export function ChildOrderDetailSection({
  data,
  type,
  parentInternalKey
}: {
  data:
    | BuyQueryResult['userGetBuyOrderByInternalKey']
    | SellQueryResult['userGetSellOrderByInternalKey']['pairBuyOrder'][number]
  type: 'buy' | 'sell'
  parentInternalKey?: string
}) {
  const router = useRouter()
  const { t } = useTranslation()
  const { open: openPaymentDetail, close: closePaymentDetail } = usePaymentDetailScreen()

  const hasRevisingLabel =
    data.status === BuyOrderStatus.complainted ||
    (data.status === BuyOrderStatus.appealing && data.dealAmount.value !== data.originDealAmount.value)

  const isVirtual = data.buyer === '0'

  function getPaymentLabel(paymentInfo: Record<string, any>) {
    const paymentMethod = getPaymentMethod(paymentInfo)
    if (paymentMethod === 'bank') {
      return `${t(`payment:${paymentMethod}`)} (${paymentInfo.bank.bankName})`
    }
    return `${t(`payment:${paymentMethod}`)} (${paymentInfo[paymentMethod].name})`
  }

  return (
    <>
      <SectionHeader>{t('orders:orderInformation')}</SectionHeader>
      <DescriptionListItem label={t('orders:systemOrderNumber')} hasBorderTop={true} canCopy={true}>
        {data.internalKey}
      </DescriptionListItem>
      {parentInternalKey && (
        <DescriptionListItem
          label={t('orders:sourceOrder')}
          onPress={() => {
            router.push({
              pathname: '/order/sell/[id]',
              params: {
                id: parentInternalKey,
                action: 'sell'
              }
            })
          }}
        >
          {parentInternalKey}
        </DescriptionListItem>
      )}
      <DescriptionListItem label={t('orders:createdAt')}>
        {type === 'sell'
          ? data.pairedAt
            ? formatDate(data.pairedAt)
            : formatDate(data.createdAt)
          : formatDate(data.createdAt)}
      </DescriptionListItem>
      {data.finishedAt !== null && (
        <DescriptionListItem label={t('orders:completedAt')}>{formatDate(data.finishedAt)}</DescriptionListItem>
      )}
      {type === 'buy' && data.sellOrderPaymentMethod && (
        <DescriptionListItem label={t('orders:seller')}>
          {Object.values(data.sellOrderPaymentMethod).find((value) => value !== null && typeof value !== 'string')
            ?.name || ''}
        </DescriptionListItem>
      )}
      {type === 'sell' && (
        <DescriptionListItem label={t('orders:buyer')}>
          {isVirtual
            ? t('orders:specialParty')
            : Object.values(data.paymentMethod).find((value) => value !== null && typeof value !== 'string')?.name ||
              ''}
        </DescriptionListItem>
      )}
      <DescriptionListItem label={t('orders:price')}>{formatMarketPrice(data.basePrice)}</DescriptionListItem>
      {type === 'buy' && (
        <>
          {![BuyOrderStatus.canceling, BuyOrderStatus.forceCanceled].includes(data.status) && (
            <DescriptionListItem
              label={t('orders:paymentAmount')}
              description={hasRevisingLabel ? t('orders:revising') : undefined}
            >
              {formatLocalFiatAmount(
                ((hasRevisingLabel ? Number(data.originDealAmount.value) : Number(data.dealAmount.value)) * Number(data.basePrice)).toString()
              )}
            </DescriptionListItem>
          )}
          <DescriptionListItem
            label={t('orders:paymentMethod')}
            onPress={() => {
              openPaymentDetail({
                children: <PaymentSectionDetail data={data.paymentMethod} type={'buy'} />,
                onClose: closePaymentDetail,
                title: t('orders:paymentMethod')
              })
            }}
          >
            {getPaymentLabel(data.paymentMethod)}
          </DescriptionListItem>
          <DescriptionListItem
            label={
              data.status === BuyOrderStatus.completed
                ? t('orders:receivedAmount')
                : [BuyOrderStatus.canceling, BuyOrderStatus.forceCanceled].includes(data.status)
                  ? t('orders:transactionQuantity')
                  : t('orders:expectedReceived')
            }
            hasBorderBottom={true}
            description={hasRevisingLabel ? t('orders:revising') : undefined}
          >
            {formatTokenAmount(hasRevisingLabel ? data.originDealAmount.value : data.dealAmount.value)}
          </DescriptionListItem>
        </>
      )}
      {type === 'sell' && (
        <>
          <DescriptionListItem
            label={t('orders:transactionQuantity')}
            hasBorderBottom={[BuyOrderStatus.canceling, BuyOrderStatus.forceCanceled].includes(data.status)}
            description={hasRevisingLabel ? t('orders:revising') : undefined}
          >
            {formatTokenAmount(hasRevisingLabel ? data.originDealAmount.value : data.dealAmount.value)}
          </DescriptionListItem>
          {![BuyOrderStatus.canceling, BuyOrderStatus.forceCanceled].includes(data.status) && (
            <DescriptionListItem
              label={t(data.status === BuyOrderStatus.completed ? 'orders:receivedAmount' : 'orders:expectedReceived')}
              isValuePrimary={true}
              hasBorderBottom={true}
              description={hasRevisingLabel ? t('orders:revising') : undefined}
            >
              {formatLocalFiatAmount(
                ((hasRevisingLabel ? Number(data.originDealAmount.value) : Number(data.dealAmount.value)) * Number(data.basePrice)).toString()
              )}
            </DescriptionListItem>
          )}
        </>
      )}
    </>
  )
}
